import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import './PriceAlertModal.css';

const PriceAlertModal = ({ 
    isOpen, 
    onClose, 
    token,
    currentPrice = null 
}) => {
    const { user } = useAuth();
    const { showToast } = useToast();
    
    const [alertType, setAlertType] = useState('price_above');
    const [targetPrice, setTargetPrice] = useState('');
    const [percentageThreshold, setPercentageThreshold] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    // Reset form when modal opens/closes or token changes
    useEffect(() => {
        if (isOpen && token) {
            setAlertType('price_above');
            setTargetPrice('');
            setPercentageThreshold('');
            setErrors({});
        }
    }, [isOpen, token]);

    const validateForm = () => {
        const newErrors = {};

        if (!token) {
            newErrors.token = 'Token information is required';
        }

        if (alertType === 'price_above' || alertType === 'price_below') {
            if (!targetPrice || isNaN(parseFloat(targetPrice)) || parseFloat(targetPrice) <= 0) {
                newErrors.targetPrice = 'Please enter a valid target price';
            }
        }

        if (alertType === 'percentage_change') {
            if (!percentageThreshold || isNaN(parseFloat(percentageThreshold)) || parseFloat(percentageThreshold) <= 0) {
                newErrors.percentageThreshold = 'Please enter a valid percentage threshold';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const alertData = {
                tokenAddress: token.address,
                tokenSymbol: token.symbol,
                tokenName: token.name,
                alertType,
                currentPrice: currentPrice || token.price,
                baselinePrice: currentPrice || token.price
            };

            if (alertType === 'price_above' || alertType === 'price_below') {
                alertData.targetPrice = parseFloat(targetPrice);
            }

            if (alertType === 'percentage_change') {
                alertData.percentageThreshold = parseFloat(percentageThreshold);
            }

            const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
            const response = await fetch('/api/alerts/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokens.access_token || ''}`
                },
                body: JSON.stringify(alertData)
            });

            const result = await response.json();

            if (result.success) {
                showToast('Price alert created successfully!', 'success');
                onClose();
            } else {
                throw new Error(result.message || 'Failed to create alert');
            }

        } catch (error) {
            console.error('Error creating price alert:', error);
            showToast(error.message || 'Failed to create price alert', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getAlertDescription = () => {
        if (!token) return '';

        const tokenName = `${token.symbol} (${token.name})`;
        const currentPriceStr = currentPrice ? `$${currentPrice.toFixed(6)}` : 'current price';

        switch (alertType) {
            case 'price_above':
                return `Alert me when ${tokenName} price goes above $${targetPrice || '___'}`;
            case 'price_below':
                return `Alert me when ${tokenName} price drops below $${targetPrice || '___'}`;
            case 'percentage_change':
                return `Alert me when ${tokenName} price changes by ${percentageThreshold || '___'}% from ${currentPriceStr}`;
            default:
                return '';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="price-alert-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Set Price Alert</h3>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                {token && (
                    <div className="token-info">
                        <div className="token-identity">
                            {token.logoURI && (
                                <img src={token.logoURI} alt={token.symbol} className="token-logo" />
                            )}
                            <div className="token-details">
                                <span className="token-symbol">{token.symbol}</span>
                                <span className="token-name">{token.name}</span>
                            </div>
                        </div>
                        {(currentPrice || token.price) && (
                            <div className="current-price">
                                Current: ${(currentPrice || token.price).toFixed(6)}
                            </div>
                        )}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="alert-form">
                    <div className="form-group">
                        <label htmlFor="alertType">Alert Type</label>
                        <select
                            id="alertType"
                            value={alertType}
                            onChange={(e) => setAlertType(e.target.value)}
                            className="form-select"
                        >
                            <option value="price_above">Price Above Target</option>
                            <option value="price_below">Price Below Target</option>
                            <option value="percentage_change">Percentage Change</option>
                        </select>
                    </div>

                    {(alertType === 'price_above' || alertType === 'price_below') && (
                        <div className="form-group">
                            <label htmlFor="targetPrice">
                                Target Price (USD)
                                {alertType === 'price_above' && currentPrice && (
                                    <span className="helper-text">
                                        (Current: ${currentPrice.toFixed(6)})
                                    </span>
                                )}
                            </label>
                            <input
                                type="number"
                                id="targetPrice"
                                value={targetPrice}
                                onChange={(e) => setTargetPrice(e.target.value)}
                                placeholder="0.000000"
                                step="0.000001"
                                min="0"
                                className={`form-input ${errors.targetPrice ? 'error' : ''}`}
                            />
                            {errors.targetPrice && (
                                <span className="error-message">{errors.targetPrice}</span>
                            )}
                        </div>
                    )}

                    {alertType === 'percentage_change' && (
                        <div className="form-group">
                            <label htmlFor="percentageThreshold">
                                Percentage Change Threshold (%)
                                <span className="helper-text">
                                    (Alert when price changes by this amount in either direction)
                                </span>
                            </label>
                            <input
                                type="number"
                                id="percentageThreshold"
                                value={percentageThreshold}
                                onChange={(e) => setPercentageThreshold(e.target.value)}
                                placeholder="10"
                                step="0.1"
                                min="0.1"
                                max="1000"
                                className={`form-input ${errors.percentageThreshold ? 'error' : ''}`}
                            />
                            {errors.percentageThreshold && (
                                <span className="error-message">{errors.percentageThreshold}</span>
                            )}
                        </div>
                    )}

                    <div className="alert-description">
                        <strong>Alert Summary:</strong>
                        <p>{getAlertDescription()}</p>
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={onClose}
                            className="cancel-btn"
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="create-alert-btn"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Creating...' : 'Create Alert'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PriceAlertModal;